import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { buildOfferCatalog } from '../seo/schemaBuilders';
import { homeServiceSchema, homeFaqSchema, homeSoftwareApplicationSchema } from '../seo/homeSchemas';
import videoSchemas from '../seo/videoSchemas.json';

const SITE_URL = 'https://www.fleettrackholland.nl';
const OG_IMAGE = `${SITE_URL}/images/og/home-og.jpg`;
import { MapPin, Truck, BarChart3, Shield, Clock, ArrowRight, Navigation2, Activity, Smartphone, ChevronRight, ClipboardList, Wrench, CheckCircle } from 'lucide-react';
import WhyFleetTrackModule from '../components/WhyFleetTrackModule';
import ROICalculator from '../components/ROICalculator';
import { trackEvent } from '../lib/analytics';
import { HeroContactCard } from '../components/TopContact';
import SnippetOptimizedFAQ from '../components/SnippetOptimizedFAQ';
import AutoplayVideo from '../components/AutoplayVideo';

const HERO_CLIPS = ['/videos/fleet-depot.mp4', '/videos/fleet-highway.mp4', '/videos/fleet-truck.mp4'];

const SHOWCASE_PHOTOS = [
  {
    src: 'bestuurder-onderweg',
    to: '/rittenregistratie/',
    kicker: 'Rittenregistratie',
    title: 'Ritten kloppen, zonder papierwerk',
    alt: 'Bestuurder in een bedrijfsbus bekijkt zijn ritten in de FleetTrack app op de smartphone',
  },
  {
    src: 'klantenservice',
    to: '/contact/',
    kicker: 'Support',
    title: 'Reactie binnen 24 uur op werkdagen',
    alt: 'Medewerker van de FleetTrack Holland klantenservice met headset aan het werk',
  },
];

const ShowcaseCaption = ({ kicker, title }) => (
  <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/80 via-slate-950/30 to-transparent px-5 pt-14 pb-5">
    <span className="block text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-300 mb-1">{kicker}</span>
    <span className="flex items-center text-lg lg:text-xl font-extrabold text-white">
      {title}
      <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
    </span>
  </div>
);

// LocalBusiness + Organization schema is emitted ONCE in public/index.html as a
// site-wide JSON-LD graph. This page intentionally does NOT re-emit it (avoids
// duplicate Organization signal that confuses Google's entity resolution).
// Per-page schemas (Service, FAQ) are built inline via schemaBuilders.

// Animate numbers with Intersection Observer.
// Default display is the final formatted target so prerender (react-snap) and
// no-JS users always see the correct value (no "0", no CLS). On the client the
// count-up runs only when the element scrolls into view.
const useCountUp = (target, duration = 1500) => {
  const [display, setDisplay] = useState(target);
  const startedRef = useRef(false);
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // Skip non-countable values like "24/7" (ratios) — keep static target.
    if (/\//.test(target)) return;

    // Dutch number formatting: "." is a thousands separator, not a decimal.
    const prefix = (target.match(/^[^\d]*/) || [''])[0];
    const suffix = (target.match(/[^\d]*$/) || [''])[0];
    const numericTarget = parseInt(target.replace(/[^\d]/g, ''), 10);
    if (isNaN(numericTarget)) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || startedRef.current) return;
        startedRef.current = true;
        observer.disconnect();

        const startTime = performance.now();
        const step = (now) => {
          const progress = Math.min((now - startTime) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          const current = Math.floor(eased * numericTarget);
          setDisplay(`${prefix}${current.toLocaleString('nl-NL')}${suffix}`);
          if (progress < 1) requestAnimationFrame(step);
          else setDisplay(target); // snap to exact original formatting
        };
        requestAnimationFrame(step);
      },
      { threshold: 0.5 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [target, duration]);

  return { display, ref };
};

const AnimatedStat = ({ value, label }) => {
  const { display, ref } = useCountUp(value);
  return (
    <div ref={ref} className="px-6 py-8 lg:py-10 text-center lg:text-left">
      <p className="text-3xl lg:text-4xl font-black text-slate-900 tabular-nums mb-1">{display}</p>
      <p className="text-slate-500 text-sm font-medium">{label}</p>
    </div>
  );
};

const HomePage = () => {
  // Conversiepad: één offerte-label op de hele pagina ("Vraag gratis offerte aan").
  const steps = [
    {
      icon: ClipboardList,
      title: 'Offerte aanvragen',
      desc: 'Vertel ons kort over uw wagenpark. Op werkdagen reageren wij binnen 24 uur met een voorstel op maat.',
    },
    {
      icon: Wrench,
      title: 'Tracker ontvangen en zelf plaatsen',
      desc: 'U ontvangt de tracker per post en installeert hem zelf in enkele minuten: klik de OBD-stekker in de OBD-poort of sluit de accu-tracker aan op de accupolen.',
    },
    {
      icon: Smartphone,
      title: 'Live overzicht',
      desc: 'Volg alles in het dashboard en de app. Maandelijks opzegbaar, met Nederlandse support op werkdagen.',
    },
  ];

  const solutionRows = [
    { icon: MapPin, title: 'Rittenregistratie', desc: 'Belastingdienst-proof en volledig automatisch.', href: '/rittenregistratie/' },
    { icon: Truck, title: 'Fleet management', desc: 'Rapportages, onderhoud en rijgedrag in één dashboard.', href: '/fleet-management/' },
    { icon: Clock, title: 'Live volgen', desc: 'Zie waar uw voertuigen zijn, in dashboard en app.', href: '/gps-tracking/' },
  ];

  const cities = [
    { slug: 'amsterdam', name: 'Amsterdam' }, { slug: 'rotterdam', name: 'Rotterdam' },
    { slug: 'den-haag', name: 'Den Haag' }, { slug: 'utrecht', name: 'Utrecht' },
    { slug: 'eindhoven', name: 'Eindhoven' }, { slug: 'groningen', name: 'Groningen' },
    { slug: 'tilburg', name: 'Tilburg' }, { slug: 'breda', name: 'Breda' },
    { slug: 'almere', name: 'Almere' }, { slug: 'nijmegen', name: 'Nijmegen' },
    { slug: 'enschede', name: 'Enschede' }, { slug: 'haarlem', name: 'Haarlem' },
    { slug: 'arnhem', name: 'Arnhem' }, { slug: 'zaanstad', name: 'Zaanstad' },
    { slug: 'apeldoorn', name: 'Apeldoorn' }, { slug: 'den-bosch', name: 'Den Bosch' },
    { slug: 'leiden', name: 'Leiden' }, { slug: 'maastricht', name: 'Maastricht' },
    { slug: 'amersfoort', name: 'Amersfoort' }, { slug: 'zwolle', name: 'Zwolle' },
  ];

  return (
    <div className="overflow-hidden">
      <Helmet defer={false}>
        <title>Fleet tracking €9,95 p/m · tracker inbegrepen | FleetTrack</title>
        <meta name="description" content="Fleet tracking voor het MKB: tracker inbegrepen, zelf installeren zonder monteur, live locatie en Belastingdienst-proof ritregistratie. Vanaf €9,95 p/m." />
        <link rel="canonical" href={`${SITE_URL}/`} />
        {/* Hreflang self-reference */}
        <link rel="alternate" hrefLang="nl" href={`${SITE_URL}/`} />
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/`} />
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/`} />
        <meta property="og:title" content="Fleet tracking €9,95 p/m · tracker inbegrepen | FleetTrack" />
        <meta property="og:description" content="GPS tracking & Belastingdienst-proof ritregistratie voor het MKB. All-in vanaf €9,95/mnd, maandelijks opzegbaar, zelf installeren in enkele minuten." />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="nl_NL" />
        <meta property="og:site_name" content="FleetTrack Holland" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="FleetTrack Holland — GPS Tracking vanaf €9,95 p/m, opzegbaar" />
        <meta name="twitter:description" content="Professioneel GPS tracking & fleet management voor MKB. Belastingdienst-proof ritregistratie." />
        <meta name="twitter:image" content={OG_IMAGE} />
        {/* JSON-LD: Service / FAQPage / SoftwareApplication komen uit src/seo/homeSchemas.js —
            dezelfde module die scripts/inject-seo-meta.js na de build gebruikt om ze terug te
            zetten wanneer de Helmet-race ze laat vallen. Data NIET hier inline zetten. */}
        <script type="application/ld+json">{JSON.stringify(homeServiceSchema())}</script>
        {/* JSON-LD: OfferCatalog (OBD-stekker + accu-tracker) */}
        <script type="application/ld+json">{JSON.stringify(buildOfferCatalog())}</script>
        <script type="application/ld+json">{JSON.stringify(homeFaqSchema())}</script>
        <script type="application/ld+json">{JSON.stringify(homeSoftwareApplicationSchema())}</script>
        {/* JSON-LD: VideoObject per homepage-video (b-roll). Zelfde JSON als scripts/inject-seo-meta.js ENSURE_JSONLD,
            want de Helmet-race laat JSON-LD op de homepage vallen. */}
        {videoSchemas.map((v) => (<script key={v['@id']} type="application/ld+json">{JSON.stringify(v)}</script>))}
      </Helmet>

      {/* ===== HERO — kalm en typografisch: geen asset, asymmetrische witruimte.
          Grote inktkop draagt de pagina; de stat-band eronder levert het bewijs. */}
      <section className="relative surface-light-grid overflow-hidden" aria-label="Hero sectie">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-8 lg:gap-y-10 items-start">
          {/* Mobiel: kop → formulierkaart → intro/CTA → video. Desktop: tekst links, kaart + video rechts. */}
          <div className="max-w-3xl lg:col-start-1 lg:row-start-1">
            <div className="inline-flex items-center px-3 py-1.5 bg-cyan-50 rounded-md border border-cyan-200 mb-8 animate-fade-in-up">
              <span className="w-1.5 h-1.5 bg-cyan-600 rounded-sm mr-2.5" aria-hidden="true" />
              <span className="text-cyan-800 text-xs font-bold uppercase tracking-[0.15em]">Vanaf €9,95 per voertuig per maand</span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-slate-900 leading-[1.03] mb-7 animate-fade-in-up animation-delay-100">
              Altijd weten waar<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-700 to-teal-600">uw voertuigen zijn</span>
            </h1>
          </div>

          <div className="lg:col-start-2 lg:row-start-1 lg:row-span-2">
            <HeroContactCard />
          </div>

          <div className="max-w-3xl lg:col-start-1 lg:row-start-2">
            <p className="text-lg lg:text-xl text-slate-600 max-w-xl mb-10 leading-relaxed animate-fade-in-up animation-delay-200">
              GPS tracking, sluitende rittenregistratie en gps vlootbeheer voor het
              Nederlandse MKB.{' '}
              <Link to="/besparingscalculator/" className="font-bold text-cyan-800 underline underline-offset-4 hover:text-cyan-900">Bereken uw besparing</Link>.
            </p>

            <div className="flex flex-col sm:flex-row sm:items-center gap-5 animate-fade-in-up animation-delay-300">
              <a
                href="#hero-contact-card"
                onClick={() => trackEvent('cta_click', { location: 'hero', cta: 'offerte' })}
                className="group inline-flex items-center justify-center px-8 py-4 bg-orange-500 text-white rounded-xl font-extrabold text-lg shadow-lg shadow-orange-900/15 hover:bg-orange-600 transition-all active:scale-95"
                id="hero-cta-offerte"
              >
                Vraag gratis offerte aan
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </a>
            </div>

            {/* Trust-strip: kernfeiten die het aanbod onderbouwen */}
            <div className="flex flex-wrap gap-3 mt-8 animate-fade-in-up animation-delay-400">
              {['Hardware inbegrepen', 'Wialon platform', 'Maandelijks opzegbaar', 'Zelf installeren'].map((b) => (
                <span key={b} className="inline-flex items-center text-sm text-slate-600 bg-slate-100 rounded-full px-3 py-1 border border-slate-200">
                  <CheckCircle className="h-3.5 w-3.5 mr-1.5 text-emerald-500" aria-hidden="true" /> {b}
                </span>
              ))}
            </div>
          </div>

          {/* Hero-showreel: depot → polderweg → snelweg bij schemer (3 × 10 s) */}
          <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-xl animate-fade-in-up animation-delay-200 lg:col-start-2 lg:row-start-3">
            <AutoplayVideo
              clips={HERO_CLIPS}
              poster="/videos/fleet-depot-poster.webp"
              className="w-full h-full object-cover aspect-video"
              label="FleetTrack Holland bedrijfswagens: vertrek vanaf het depot, onderweg over de polder en op de snelweg"
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/70 to-transparent px-5 pt-10 pb-4">
              <span className="inline-flex items-center text-xs font-bold uppercase tracking-[0.15em] text-white">
                <MapPin className="h-3.5 w-3.5 mr-2 text-cyan-300" aria-hidden="true" />
                Uw wagenpark, altijd in beeld
              </span>
            </div>
          </div>
          </div>
        </div>
      </section>

      {/* ===== IN BEELD — video van het dashboard + foto's van onderweg en support ===== */}
      <section className="bg-white" aria-label="FleetTrack Holland in beeld">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 lg:grid-rows-2 gap-4 lg:gap-5">
            <Link to="/gps-tracking/" className="group relative rounded-3xl overflow-hidden border border-slate-200 shadow-xl lg:col-span-2 lg:row-span-2 bg-slate-900">
              <AutoplayVideo
                clips={['/videos/fleet-office.mp4']}
                poster="/videos/fleet-office-poster.webp"
                width={1280}
                height={856}
                className="w-full h-full object-cover aspect-video transition-transform duration-700 group-hover:scale-[1.03]"
                label="Wagenparkbeheerder volgt bedrijfswagens op de live kaart van Nederland"
              />
              <ShowcaseCaption kicker="Live kaart" title="Al uw voertuigen op één scherm" />
            </Link>
            {SHOWCASE_PHOTOS.map((p) => (
              <Link key={p.src} to={p.to} className="group relative rounded-3xl overflow-hidden border border-slate-200 shadow-xl bg-slate-900">
                <picture>
                  <source srcSet={`/images/photos/${p.src}.avif 1x, /images/photos/${p.src}@2x.avif 2x`} type="image/avif" />
                  <source srcSet={`/images/photos/${p.src}.webp 1x, /images/photos/${p.src}@2x.webp 2x`} type="image/webp" />
                  <img src={`/images/photos/${p.src}.jpg`} alt={p.alt} loading="lazy" decoding="async" width={1024} height={687} className="w-full h-full object-cover aspect-video transition-transform duration-700 group-hover:scale-[1.05]" />
                </picture>
                <ShowcaseCaption kicker={p.kicker} title={p.title} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== STAT-BAND — hairline kolommen, alleen verifieerbare beloften ===== */}
      <section className="bg-white border-y border-slate-200" aria-label="Kerncijfers">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 lg:divide-x divide-slate-200">
            <AnimatedStat value="Gratis" label="Besparing berekenen met de calculator" />
            <AnimatedStat value="Zelf" label="Installeren in enkele minuten" />
            <AnimatedStat value="24u" label="Reactietijd op werkdagen" />
            <AnimatedStat value="100%" label="Maandelijks opzegbaar" />
          </div>
        </div>
      </section>

      {/* ===== OPLOSSINGEN — asymmetrisch: featured + compacte rijen ===== */}
      <section className="py-24 bg-slate-50" aria-labelledby="solutions-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <h2 id="solutions-heading" className="text-4xl lg:text-5xl font-extrabold text-slate-900 mb-4">
              Eén platform voor uw hele wagenpark
            </h2>
            <p className="text-lg text-slate-600">
              Van live tracking en gps vlootbeheer tot fiscale administratie. U kiest alleen wat u nodig heeft.
              Onze klanten maken gebruik van realtime data om betere beslissingen te nemen, operationele kosten
              te verlagen en zo hun gps vlootbeheer volledig in eigen hand te houden.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Featured: GPS Tracking */}
            <Link
              to="/gps-tracking/"
              className="lg:col-span-2 group relative bg-cyan-50 border border-cyan-100 rounded-2xl p-10 lg:p-12 flex flex-col justify-between min-h-[320px] hover:border-cyan-300 hover:shadow-xl hover:shadow-cyan-900/5 transition-all duration-300"
            >
              <div>
                <div className="w-12 h-12 rounded-xl bg-white border border-cyan-200 flex items-center justify-center mb-6">
                  <Navigation2 className="h-6 w-6 text-cyan-700" aria-hidden="true" />
                </div>
                <h3 className="text-3xl font-extrabold text-slate-900 mb-3 group-hover:text-cyan-800 transition-colors">GPS tracking</h3>
                <p className="text-slate-600 leading-relaxed max-w-lg">
                  Zie elk voertuig live op de kaart, met diefstalbeveiliging, geofencing
                  en slimme alerts. Voor 2 of voor 200 voertuigen.
                </p>
              </div>
              <span className="inline-flex items-center text-cyan-800 font-bold mt-8 group-hover:translate-x-1.5 transition-transform">
                Bekijk GPS tracking <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </span>
            </Link>

            {/* Compacte rijen */}
            <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
              {solutionRows.map((row) => (
                <Link key={row.title} to={row.href} className="group flex items-start gap-4 p-6 hover:bg-slate-50 transition-colors first:rounded-t-2xl last:rounded-b-2xl">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <row.icon className="h-5 w-5 text-slate-600" aria-hidden="true" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-900 group-hover:text-cyan-800 transition-colors">{row.title}</p>
                    <p className="text-sm text-slate-500 mt-0.5">{row.desc}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-cyan-700 mt-1.5 flex-shrink-0 transition-colors" aria-hidden="true" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== HOE HET WERKT — proces in drie bewegingen ===== */}
      <section className="py-24 bg-white" aria-labelledby="werkwijze-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="werkwijze-heading" className="text-4xl lg:text-5xl font-extrabold text-slate-900 mb-14 max-w-2xl">
            Binnen een dag van offerte naar live overzicht
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-2xl overflow-hidden">
            {steps.map((step) => (
              <div key={step.title} className="bg-white p-8 lg:p-10">
                <div className="w-11 h-11 rounded-lg bg-cyan-50 border border-cyan-100 flex items-center justify-center mb-5">
                  <step.icon className="h-5 w-5 text-cyan-700" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-extrabold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FUNCTIES — bento met visuele variatie ===== */}
      <section className="py-24 bg-slate-50" aria-labelledby="features-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="features-heading" className="text-4xl lg:text-5xl font-extrabold text-slate-900 mb-14 max-w-2xl">
            Alles wat een wagenparkbeheerder nodig heeft
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Breed: donkere contrastcel met foto van de live kaart */}
            <Link to="/fleet-management/" className="md:col-span-2 group relative overflow-hidden bg-[#083344] rounded-2xl p-7 flex flex-col justify-end min-h-[260px]">
              <picture>
                <source srcSet="/images/photos/fleet-dashboard-office.avif 1x, /images/photos/fleet-dashboard-office@2x.avif 2x" type="image/avif" />
                <source srcSet="/images/photos/fleet-dashboard-office.webp 1x, /images/photos/fleet-dashboard-office@2x.webp 2x" type="image/webp" />
                <img src="/images/photos/fleet-dashboard-office.jpg" alt="Wagenparkbeheerder ziet alle voertuigen live op de kaart van Nederland" loading="lazy" decoding="async" width={1024} height={687} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
              </picture>
              <div className="absolute inset-0 bg-gradient-to-t from-[#083344] via-[#083344]/70 to-transparent" aria-hidden="true" />
              <div className="relative">
                <Navigation2 className="h-8 w-8 text-cyan-300 mb-4" aria-hidden="true" />
                <h3 className="text-xl font-extrabold text-white mb-1">Realtime kaart</h3>
                <p className="text-cyan-100/90 text-sm">Live posities met updates elke 10 seconden.</p>
              </div>
            </Link>

            {/* Breed: tintcel */}
            <Link to="/fleet-management/" className="md:col-span-2 group bg-cyan-50 border border-cyan-100 rounded-2xl p-7 flex flex-col justify-end min-h-[260px] hover:border-cyan-300 transition-all duration-300">
              <Smartphone className="h-8 w-8 text-cyan-700 mb-4" aria-hidden="true" />
              <h3 className="text-xl font-extrabold text-slate-900 mb-1 group-hover:text-cyan-800 transition-colors">App en webdashboard</h3>
              <p className="text-slate-600 text-sm">Beheer uw vloot vanaf elk apparaat, zonder installatie.</p>
            </Link>

            {/* 4 smalle witte cellen */}
            {[
              { icon: BarChart3, title: 'Rapportages', desc: 'Ritten, brandstof en kosten. Export naar PDF of Excel.', href: '/fleet-management/' },
              { icon: Shield, title: 'Geofencing', desc: 'Automatische melding bij in- of uitrijden van zones.', href: '/gps-tracking/' },
              { icon: Activity, title: 'Rijgedrag', desc: 'Scores op remmen, optrekken en snelheid per chauffeur.', href: '/fleet-management/' },
              { icon: ClipboardList, title: 'Rittenregistratie', desc: 'Automatische ritadministratie voor de Belastingdienst.', href: '/rittenregistratie/' },
            ].map((f) => (
              <Link key={f.title} to={f.href} className="group bg-white border border-slate-200 rounded-2xl p-7 hover:border-cyan-300 hover:shadow-lg transition-all duration-300">
                <f.icon className="h-6 w-6 text-cyan-700 mb-4" aria-hidden="true" />
                <h3 className="font-extrabold text-slate-900 mb-1 group-hover:text-cyan-800 transition-colors">{f.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WAAROM FLEETTRACK ===== */}
      <WhyFleetTrackModule />

      {/* ===== ROI CALCULATOR ===== */}
      <ROICalculator />

      {/* ===== FAQ ===== */}
      <SnippetOptimizedFAQ />

      {/* ===== SERVICEGEBIED — compacte linkkolommen (SEO: 20 stadspaginas) ===== */}
      <section className="py-20 bg-white border-t border-slate-100" aria-labelledby="servicegebied-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="servicegebied-heading" className="text-3xl lg:text-4xl font-extrabold text-slate-900 mb-3">
            GPS tracking door heel Nederland
          </h2>
          <p className="text-slate-600 mb-10 max-w-xl">
            Zelf installeren, van Groningen tot Maastricht.
          </p>
          <ul className="columns-2 sm:columns-3 lg:columns-5 gap-8 [&>li]:mb-3">
            {cities.map((city) => (
              <li key={city.slug}>
                <Link
                  to={`/${city.slug}/`}
                  className="text-slate-600 hover:text-cyan-800 font-medium text-sm transition-colors"
                >
                  GPS tracking {city.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ===== AFSLUITENDE CTA — één donker petrol-blok, aansluitend op de footer ===== */}
      <section className="bg-[#083344] py-20" aria-labelledby="final-cta-heading">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 id="final-cta-heading" className="text-4xl lg:text-5xl font-extrabold text-white mb-5">
            Klaar om te starten?
          </h2>
          <p className="text-lg text-cyan-100/80 mb-10">
            Gratis en vrijblijvend. Reactie binnen 24 uur op werkdagen, zelf installeren in enkele minuten.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/offerte/"
              onClick={() => trackEvent('cta_click', { location: 'final', cta: 'offerte' })}
              className="inline-flex items-center justify-center px-9 py-4 bg-orange-500 text-white rounded-xl font-extrabold text-lg shadow-lg hover:bg-orange-600 transition-all active:scale-95"
              id="final-cta-offerte"
            >
              Vraag gratis offerte aan <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
            </Link>
          </div>
          <p className="mt-10 text-sm text-cyan-100/60">
            Al klant?{' '}
            <a href="https://portal.fleettrackholland.nl" className="underline underline-offset-4 hover:text-white transition-colors">
              Log in op het klantenportaal
            </a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
